import React, { useState } from 'react';
import SectionHeader from '../components/SectionHeader';
import RestaurantGallery from '../components/RestaurantGallery';
import ReviewCarousel from '../components/ReviewCarousel';
import OptimizedImage from '../components/OptimizedImage';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { ZoomIn } from 'lucide-react';

const RestaurantPage: React.FC = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const breakfastImages = [
    { src: '/images/food/breakfast-full.webp', alt: 'Full Scottish Breakfast' },
    { src: '/images/food/breakfast-menu.webp', alt: 'Breakfast Menu' },
    { src: '/images/food/breakfast-promo.webp', alt: 'Breakfast at The Creamery' }
  ];

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="pt-20 bg-primary-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <SectionHeader
          eyebrow="Family Dining Experience"
          title="Our Restaurant"
          subtitle="Our restaurant offers a friendly atmosphere where modern culinary techniques meet traditional flavours. Whether you're joining us for a casual lunch or celebrating a special occasion, our chef's seasonal menu promises to delight your palate. (Don't forget to check out our SPECIALS BOARD)"
          darkMode={true}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-primary-gold mb-6 font-display">
              Great Food, Great Atmosphere
            </h3>
            <p className="text-gray-300 mb-6">
              Our restaurant offers a friendly atmosphere where modern culinary techniques meet traditional flavours. Whether you're joining us for a casual lunch or celebrating a special occasion, our chef's seasonal menu promises to delight your palate. (Don't forget to check out our SPECIALS BOARD)
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <span className="text-primary-gold mr-3">✓</span>
                <span className="text-gray-300">Locally sourced ingredients</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-gold mr-3">✓</span>
                <span className="text-gray-300">Seasonal menu updated quarterly</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-gold mr-3">✓</span>
                <span className="text-gray-300">Extensive wine and cocktail selection</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-gold mr-3">✓</span>
                <span className="text-gray-300">Family dining - children welcome</span>
              </li>
            </ul>
          </div>
          <div className="bg-gray-900 rounded-lg h-96 flex items-center justify-center overflow-hidden">
            <OptimizedImage 
              src="/images/restaurant/full-dining-room.jpg" 
              alt="Restaurant" 
              className="w-full h-full object-cover"
              width={1600}
              height={1200}
            />
          </div>
        </div>

        {/* Breakfast Section */}
        <div className="my-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="overflow-hidden rounded-lg">
              <OptimizedImage 
                src="/images/food/breakfast-full.webp" 
                alt="Full Scottish Breakfast" 
                className="w-full h-full object-cover"
                width={800}
                height={600}
              />
            </div>
            <div>
              <h3 className="text-4xl font-bold text-primary-gold mb-6 font-display">
                Breakfast at The Creamery
              </h3>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                Start your day the traditional way with a hearty Creamery breakfast, made with quality ingredients and cooked to order. From perfectly cooked eggs and crispy bacon to linked sausages, square sausage, tattie scones, grilled tomatoes, beans and toast, it's a comforting Scottish favourite with everything you need to set you up for the day.
              </p>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                Whether you're joining us for a leisurely breakfast or fuelling up for a busy day ahead with our takeaway service, The Creamery Breakfast is a delicious taste of home, served fresh and with a warm welcome.
              </p>
              
              {/* Additional Breakfast Images */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div 
                  className="relative overflow-hidden rounded-lg cursor-pointer group"
                  onClick={() => openLightbox(1)}
                >
                  <OptimizedImage 
                    src="/images/food/breakfast-menu.webp" 
                    alt="Breakfast Menu" 
                    className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-110"
                    width={400}
                    height={300}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
                <div 
                  className="relative overflow-hidden rounded-lg cursor-pointer group"
                  onClick={() => openLightbox(2)}
                >
                  <OptimizedImage 
                    src="/images/food/breakfast-promo.webp" 
                    alt="Breakfast at The Creamery" 
                    className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-110"
                    width={400}
                    height={300}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-300 flex items-center justify-center">
                    <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-primary-black border border-primary-gold rounded-lg p-8 max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-primary-gold mb-6 text-center font-display">
            Opening Hours
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center md:text-left">
            <div>
              <h4 className="font-semibold text-white mb-3 uppercase tracking-wider text-sm">Kitchen</h4>
              <div className="space-y-2 text-gray-300">
                <p>Sunday - Friday: 12pm - 7pm</p>
                <p>Saturday: 12pm - 7:30pm</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3 uppercase tracking-wider text-sm">Contact</h4>
              <div className="space-y-2 text-gray-300">
                <p>Phone: 01324 636330</p>
                <p>Email: enquiries@thecreamerysportsbar.co.uk</p>
              </div>
            </div>
            </div>
            <p className="text-gray-300 text-center mt-4">Reservations recommended</p>
          
        </div>

        <RestaurantGallery />

        <ReviewCarousel type="restaurant" />
      </div>

      

      {/* Reservation CTA Section */}
      <section className="relative py-20 bg-primary-black overflow-hidden">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-gold opacity-5 rounded-full blur-3xl"></div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-primary-gold mb-6 font-display">
            Reserve Your Table
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Call us to book your table for an unforgettable dining experience
          </p>
          <a 
            href="/book" 
            className="inline-block bg-primary-gold text-primary-black px-12 py-4 rounded-full font-semibold text-lg hover:bg-primary-black hover:text-primary-gold hover:border-2 hover:border-primary-gold transition-all"
            aria-label="Make a restaurant reservation"
          >
            Make a Reservation
          </a>
        </div>
      </section>

      {/* Lightbox for Breakfast Images */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={breakfastImages}
      />
    </div>
  );
};

export default RestaurantPage;
